import React, { useEffect, useState, memo } from "react";
import {
  FormGroup,
  Label,
  Input,
  Button,
  Spinner,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import {
  useDetailQuery,
  useCreateMutation,
  useUpdateMutation,
} from "helpers/http-client/hooks/useCrud";
import { API_ENDPOINTS } from "helpers/http-client/httpConstants";

interface PackageFormProps {
  packageSizeId?: string;
  onSuccess: (pkg: any) => void;
}

export interface PackageFormValues {
  description: string;
  unitCount: number;
  unitType: string;
}

const PackageForm: React.FC<PackageFormProps> = ({ packageSizeId, onSuccess }) => {
  const { t } = useTranslation();
  const isEdit = Boolean(packageSizeId);
  const url = isEdit ? API_ENDPOINTS.PACKAGE_DETAILS : "";

  const { data, isLoading } = useDetailQuery<PackageFormValues>(url, packageSizeId);

  const [formValues, setFormValues] = useState<PackageFormValues>({
    description: "",
    unitCount: 0,
    unitType: "",
  });

  const { mutate: createPackage } = useCreateMutation(API_ENDPOINTS.PACKAGE_DETAILS);
  const { mutate: updatePackage } = useUpdateMutation(API_ENDPOINTS.PACKAGE_DETAILS);

  useEffect(() => {
    if (data && isEdit) {
      setFormValues({
        description: data.description || "",
        unitCount: data.unitCount ?? 0,
        unitType: data.unitType || "",
      });
    }
  }, [data, isEdit]);

  const handleChange = (field: keyof PackageFormValues, value: string | number) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: field === "unitCount" ? Number(value) : value,
    }));
  };

  const handleSubmit = () => {
    const payload: PackageFormValues = {
      description: formValues.description,
      unitCount: Number(formValues.unitCount),
      unitType: formValues.unitType,
    };

    if (isEdit && packageSizeId) {
      updatePackage({ id: packageSizeId, data: payload }, { onSuccess });
    } else {
      createPackage(payload, { onSuccess });
    }
  };

  if (isEdit && isLoading) {
    return <Spinner color="primary" />;
  }

  return (
    <>
      <FormGroup>
        <Label>
          {t("Description")}{" "}
          <span
            className="text-primary"
            style={{ cursor: "help" }}
            title="A short, human-readable name for the package. Example: &quot;Box of 10 Tablets&quot; or &quot;Bottle of 120ml&quot;."
          >
            ?
          </span>
        </Label>
        <Input
          value={formValues.description}
          placeholder={t("e.g., Box of 10 Tablets")}
          onChange={(e) => handleChange("description", e.target.value)}
        />
      </FormGroup>

      <FormGroup>
        <Label>
          {t("Unit Count")}{" "}
          <span
            className="text-primary"
            style={{ cursor: "help" }}
            title="The number of units inside this package. For example, 10 tablets or 120ml of syrup."
          >
            ?
          </span>
        </Label>
        <Input
          type="number"
          value={formValues.unitCount}
          placeholder={t("e.g., 10")}
          onChange={(e) => handleChange("unitCount", e.target.value)}
        />
      </FormGroup>

      <FormGroup>
        <Label>
          {t("Unit Type")}{" "}
          <span
            className="text-primary"
            style={{ cursor: "help" }}
            title="Type of unit included in the package. Examples: Tablet, Capsule, ml, Syrup, etc."
          >
            ?
          </span>
        </Label>
        <Input
          value={formValues.unitType}
          placeholder={t("e.g., Tablet, ml, Capsule")}
          onChange={(e) => handleChange("unitType", e.target.value)}
        />
      </FormGroup>

      <div className="d-flex justify-content-end">
        <Button color="primary" onClick={handleSubmit}>
          {isEdit ? t("Update") : t("Save")}
        </Button>
      </div>
    </>
  );
};

export default memo(PackageForm);
